import Immutable from 'immutable';
import isEqual from 'lodash/isEqual';
import type { AudioSourceData } from '@lumen5/luminary';

import type {
    DAMSearchRequest,
    SuggestedMediaRequest,
    SuggestedMediaResponse,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AudioSearchSourceEnum,
    FiltersEnum,
    ProviderEnum,
    SlideBackgroundModelTypeEnum,
    TaskResultMessageTypeEnum,
} from 'js/src/client';
import type {
    AssetContainerModel,
    AudioAssetModel,
    ImageGenerationRequestModelEnum,
    ModelNameEnum,
    SlideBackgroundModel,
    SupportedAIMediaAspectRatios,
    ToggleFavoritedAsset,
    VideoGenerationRequestModelEnum,
} from 'js/src/client/types.gen';
import {
    AssetTypes,
    MediaSearchSourceEnum,
    SimilarAssetSourceTypeEnum,
    SourceEnum,
    TaskResultStatusEnum,
} from 'js/src/client/types.gen';
import { Toast } from 'js/src/components/common/Toast';
import { EDITOR_TAB_OPTIONS } from 'js/src/data/app';
import { logSearch } from 'js/src/data/click-logs';
import type {
    EditorDAMTabContextsType,
    SearchContextValueType,
    SearchResponse,
    UploadSearchContextType,
} from 'js/src/data/media';
import {
    addNewFolder,
    AI_MEDIA_SUB_TAB_OPTIONS,
    changeActiveSearchContext,
    changeCorrectedMediaSearchTerm,
    changeMediaSearchTerm,
    changeRelatedMediaSearchTerms,
    changeSuggestedMediaSearchTerm,
    convertImmutableSlideBackgroundToImmutableAssetContainer,
    DAM_SEARCH_CONTEXT_CONFIG,
    deleteAudioSuccess,
    deleteMediaResult,
    EDITOR_ASSET_COLLECTION_INFORMATION,
    FILTER_BY_LEVELS,
    getActiveAudioDurationFilter,
    getActiveCorrectedMediaSearchTerm,
    getActiveMediaFilterByUserLevel,
    getActiveMediaFilters,
    getActiveMediaNext,
    getActiveMediaPage,
    getActiveMediaPos,
    getActiveMediaSort,
    getActiveSearchContextForCurrentTab,
    getActiveSearchContextForTab,
    getActiveSearchTerm,
    getActiveStickerType,
    getAIMediaModels,
    getAssetTypeForSearchContext,
    getAssetUuidFromAssetContainer,
    getFolderDetailsById,
    getFolderDetailsByNameAndSource,
    getFrequentlyUsedTags,
    getGenerationFromMediaUuid,
    getIsFavouriteMedia,
    getIsInitializingImageGeneration,
    getIsInitializingVideoGeneration,
    getIsSearchInProgress,
    getIsSimilarSearchContext,
    getLastUsedFilters,
    getMediaResultCount,
    getPlayingTrackUrl,
    getSimilarSearchOriginalAssetUuid,
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isDAMSearchContext,
    isImmutableAudioAsset,
    isImmutableAudioSourceData,
    isMediaSearchResponse,
    MEDIA_TYPE_OPTIONS,
    receiveDamConnectionError,
    receiveMediaError,
    receiveMediaResponse,
    RECORDING_FOLDER_NAME,
    resetFolderSearchState,
    saveAIMedia,
    SCRIPT_COMPOSER_MEDIA_FOLDER_NAME,
    SEARCH_CONTEXT,
    selectActiveMediaFolder,
    selectFolderByName,
    selectRecordingFolder,
    setActiveAIMediaSubTab,
    setActiveFolder,
    setAIMediaModels,
    setAISimilarGenerations,
    setAllFavorites,
    setCurrentAudioPlayingStatus,
    setCurrentMediaGenerations,
    setCurrentMediaQueryScene,
    setCurrentPlayingAudioDuration,
    setCurrentPlayingAudioScrubberSeekPercent,
    setCurrentPlayingAudioSeekPercent,
    setCurrentPlayingAudioUrl,
    setIsFetchingRecommendedSearchTerms,
    setIsInitializingImageGeneration,
    setIsInitializingVideoGeneration,
    setIsMediaQuerySearchLoading,
    setIsSimilarSearchContext,
    setIsSimilarSearchLoading,
    setMediaQuerySearchResults,
    setRecommendedSearchTerms,
    setSavingAIMedia,
    setSimilarSearchOriginalAsset,
    setSimilarSearchResults,
    setUploadsFolderStore,
    setVideoGenerationTask,
    showMediaQuerySuggestionError,
    startMediaSearch,
    storeAllFolders,
    storeFrequentlyUsedTags,
    toggleFavorite,
    toggleFilterByUserLevel,
} from 'js/src/data/media';
import { getRecommendedVideoResponseMediaCandidates } from 'js/src/data/recommendations';
import { getAllSessionUploadedAssetUuids } from 'js/src/data/uploads/uploads-selectors';
import {
    type FileWithMetadata,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
    type UploadOptions,
} from 'js/src/data/uploads/uploads-types';
import { loadUsageLimits } from 'js/src/data/usage-limit/usage-limit-operations';
import { getUsageLimits } from 'js/src/data/usage-limit/usage-limit-selectors';
import {
    updateAIMediaCreditsUsedInCycle,
    updateImageGenerationsUsedInCycle,
} from 'js/src/data/usage-limit/usage-limit-slice';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, ImmutableToJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isApiError, isApiErrorWithProperty } from 'js/src/types';

export const triggerMediaSearch = (
    search_context: SearchContextValueType,
    append_results: boolean,
    duration: number,
    autocorrect = true,
    folderId: number | null = null,
) => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // parse the search params out of the state
        const state = getState();
        const page = append_results === true ? getActiveMediaPage(state, search_context) : 1;
        const pos = append_results === true ? getActiveMediaPos(state, search_context) : null;
        const corrected_search_term = getActiveCorrectedMediaSearchTerm(state, search_context);
        const lastUsedFilters = getLastUsedFilters(state, search_context);
        let search_term = getActiveSearchTerm(state, search_context);
        const activeMediaSourceFilters = getActiveMediaFilters(state, search_context);
        const sort = getActiveMediaSort(state, search_context); // selector selects based on folder_id
        // for now we only have 2 filter levels: user vs workspace
        const filter_by_user_level =
            getActiveMediaFilterByUserLevel(state, search_context) ===
            FILTER_BY_LEVELS.filter_by_user_level;
        const audioDurationFilter = getActiveAudioDurationFilter(state, search_context);
        const searchInProgress = getIsSearchInProgress(state, search_context);
        const numExistingResults = getMediaResultCount(state, search_context);
        const isSimilarSearchContext = getIsSimilarSearchContext(state);
        let did_you_mean = true;

        if (searchInProgress) {
            // If the filters were unchanged, continue to abort the search. Otherwise, allow new search since it's a new filter list.
            if (isEqual(activeMediaSourceFilters, lastUsedFilters)) {
                // we are already in the middle of a search,
                // let's wait for that one
                // our UI should be always scrollable, so user just needs to scroll to trigger another search
                return;
            }
        }
        const isSearchTermEmpty = search_term === '' || search_term.replace(/\s/g, '').length === 0;
        search_term = isSearchTermEmpty ? '' : search_term;
        const is_audio_search =
            getAssetTypeForSearchContext(search_context) === AssetContainerTypes.AUDIO;

        // determine the search source
        let search_source = EDITOR_ASSET_COLLECTION_INFORMATION[search_context].search_source;

        if (isSearchTermEmpty) {
            search_source =
                EDITOR_ASSET_COLLECTION_INFORMATION[search_context].no_query_search_source;
        }

        let uuids: string[] = [];
        if (search_source === MediaSearchSourceEnum.UPLOADS) {
            // the UUIDs are 36 chars long and we don't want the body to be HUGE so limit to 100 UUIDs
            uuids = ImmutableToJS(getAllSessionUploadedAssetUuids(state).take(100));
        } else if (search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS && search_term === '') {
            // populate the UUIDs for recommended default assets based on media candidates
            const recommendedMediaCandidates = getRecommendedVideoResponseMediaCandidates(state);
            if (recommendedMediaCandidates && recommendedMediaCandidates.get('stock_media')) {
                // take max 100 uuids (see above logic)
                const stock_media_uuids = ImmutableToJS(
                    recommendedMediaCandidates.get('stock_media'),
                );
                uuids = stock_media_uuids.slice(0, 100);
            }
        }

        let q = search_term;
        if (page > 1 && corrected_search_term !== '') {
            q = corrected_search_term;
            autocorrect = false;
            did_you_mean = false;
        }
        if (isSearchTermEmpty) {
            autocorrect = false;
            did_you_mean = false;
        }

        if (isSimilarSearchContext) {
            // Clear similar search context so that it doesn't override new search
            dispatch(setIsSimilarSearchContext(false));
            dispatch(
                setSimilarSearchResults({
                    results: Immutable.List<ImmutableMap<AssetContainerModel>>(),
                }),
            );
            dispatch(setSimilarSearchOriginalAsset({ originalAsset: null }));
        }

        const sources_filter = [];
        if (is_audio_search) {
            sources_filter.push(SourceEnum.SOURCE_AUDIOBLOCKS);
        }

        const data: any = {
            q,
            search_source,
            page,
            filter: activeMediaSourceFilters.join(','),
            filter_by_user_level,
            sources_filter: sources_filter,
            autocorrect,
            did_you_mean,
            sort,
            uuids,
            pos,
        };

        if (search_source === MediaSearchSourceEnum.FREEPIK_STICKERS) {
            data.icon_type = getActiveStickerType(state, search_context);
        }

        // Add audio duration filtering if it's an audio search and user has set custom duration
        if (is_audio_search) {
            if (audioDurationFilter.min > 0) {
                data.duration_min = audioDurationFilter.min;
            }
            if (audioDurationFilter.max !== null) {
                data.duration_max = audioDurationFilter.max;
            }
            // special case: for default music search (query is empty) and both min/max are unset, actually
            // use the video duration so we only show results that are at least as long as the video
            if (
                search_source === AudioSearchSourceEnum.CC &&
                audioDurationFilter.min === 0 &&
                audioDurationFilter.max === null
            ) {
                data.duration_min = Math.floor(duration / 1000);
                // cap this filter to 5 minutes since we don't have many tracks longer than that
                data.duration_min = Math.min(data.duration_min, 300);
            }
        }

        if (folderId) {
            data['folder_id'] = folderId;
        }

        const onSearchSuccess = (resp: SearchResponse) => {
            const response = resp;

            if (response.error && response.error !== 'no_access') {
                Toast.error(response.error);
            }

            let { corrected_query } = response;
            const {
                corrected_query_indices,
                did_you_mean_query = '',
                did_you_mean_highlighted_query = '',
            } = response;
            let popular_queries: string[] = [];
            let processed_query = search_term;
            let pos = null;
            if (isMediaSearchResponse(response)) {
                if (response.popular_queries) {
                    popular_queries = response.popular_queries;
                }
                if (response.processed_query) {
                    processed_query = response.processed_query;
                }
                if (response.pos) {
                    pos = response.pos;
                }
            }
            if (corrected_query == null) {
                corrected_query = '';
            }
            if (page === 1) {
                dispatch(
                    changeCorrectedMediaSearchTerm({
                        search_context,
                        search_term: corrected_query,
                        search_term_indices: corrected_query_indices ?? [],
                    }),
                );
                dispatch(
                    changeSuggestedMediaSearchTerm({
                        search_context,
                        query: did_you_mean_query,
                        highlighted_query: did_you_mean_highlighted_query,
                    }),
                );
                dispatch(
                    changeRelatedMediaSearchTerms({
                        search_context,
                        popular_queries,
                    }),
                );
            }
            if (pos === undefined) {
                pos = null;
            }
            // log the search to our analytics services
            dispatch(
                logSearch(
                    response,
                    search_term,
                    processed_query,
                    page,
                    search_context,
                    is_audio_search,
                    activeMediaSourceFilters,
                    corrected_search_term,
                ),
            );
            dispatch(
                receiveMediaResponse({
                    response,
                    page,
                    search_context,
                    append_results,
                    num_existing_results: numExistingResults,
                    folderId, // if !!folderId the action routes to all_uploads_folders reducer, see data/media/index.ts
                    pos,
                }),
            );
        };

        // Dispatch startMediaSearch BEFORE making the API call to prevent race conditions.
        // This ensures subsequent calls will see we're making a request and early-return,
        // preventing duplicate requests for the same page.
        dispatch(setCurrentMediaQueryScene({ sceneId: null }));
        dispatch(
            startMediaSearch({
                search_term,
                page,
                search_context,
                filter: activeMediaSourceFilters,
                append_results,
                folderId,
            }),
        );

        if (is_audio_search) {
            ApiService.audioRetrieve({ query: data })
                .then((response) => {
                    onSearchSuccess(response.data);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    Toast.error('Something went wrong retrieving search results.');
                    dispatch(receiveMediaError({ search_context, folderId }));
                });
        } else {
            ApiService.mediaSearchCreate({ body: data })
                .then((response) => {
                    onSearchSuccess(response.data);
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    Toast.error('Something went wrong retrieving search results.');
                    dispatch(receiveMediaError({ search_context, folderId }));
                });
        }

        let analytics_event = 'Media Search: Search Triggered';
        if (is_audio_search === true) {
            analytics_event = 'Media Stage: Audio Search Triggered';
        }
        // only trigger the search if the term isn't blank and for the first page
        if (search_term !== '' && page === 1) {
            analytics.track(analytics_event, {
                term: search_term,
                scope: search_context,
            });
        }
    };
};

export const triggerDAMSearch = (
    searchContext: SearchContextValueType,
    appendResults: boolean,
    duration: number,
    autocorrect = true,
    folderId: number | null = null,
) => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();

        const searchInProgress = getIsSearchInProgress(state, searchContext);
        if (searchInProgress) {
            return;
        }

        const page = appendResults === true ? getActiveMediaPage(state, searchContext) : 1;
        const next = appendResults === true ? getActiveMediaNext(state, searchContext) : null;
        const searchTerm = getActiveSearchTerm(state, searchContext);
        const activeMediaFilters = getActiveMediaFilters(state, searchContext);
        const numExistingResults = getMediaResultCount(state, searchContext);

        const damConfig = DAM_SEARCH_CONTEXT_CONFIG[searchContext as EditorDAMTabContextsType];
        const request: DAMSearchRequest = {
            dam_config_id: damConfig.id,
            provider: damConfig.provider,
            filters: [],
        };
        if (searchTerm) {
            request.search_term = searchTerm;
        }
        if (next) {
            request.next_page = next;
        }
        if (activeMediaFilters.length > 0) {
            request.filters = activeMediaFilters.reduce((acc, cur) => {
                switch (cur) {
                    case MEDIA_TYPE_OPTIONS.PHOTOS:
                        acc.push(FiltersEnum.PHOTOS);
                        break;
                    case MEDIA_TYPE_OPTIONS.VIDEOS:
                        acc.push(FiltersEnum.VIDEOS);
                        break;
                    case MEDIA_TYPE_OPTIONS.ALL:
                        acc.push(FiltersEnum.PHOTOS, FiltersEnum.VIDEOS);
                        break;
                    default:
                        break;
                }
                return acc;
            }, [] as FiltersEnum[]);
        }
        // Dispatch startMediaSearch BEFORE making the API call to prevent race conditions
        dispatch(
            setCurrentMediaQueryScene({
                sceneId: null,
            }),
        );
        dispatch(
            startMediaSearch({
                search_term: searchTerm,
                page,
                search_context: searchContext,
                filter: activeMediaFilters,
                append_results: appendResults,
                folderId,
            }),
        );

        ApiService.damSearchCreate({ body: request })
            .then((response) => {
                dispatch(
                    receiveMediaResponse({
                        response: response.data,
                        search_context: searchContext, // required to route action to correct reducer
                        num_existing_results: numExistingResults, // used for determining "no results found"
                        page, // always include page number for infinite scroll to work correctly
                        pos: null, // only used for TENOR search, but is required property
                        folderId,
                        append_results: appendResults,
                    }),
                );
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                if (
                    damConfig.provider === ProviderEnum.BYNDER &&
                    isApiError(error) &&
                    error.status >= 400 &&
                    error.status < 500
                ) {
                    dispatch(
                        receiveDamConnectionError({
                            search_context: searchContext,
                        }),
                    );
                } else {
                    let message = 'Something went wrong retrieving search results.';
                    if (isApiErrorWithProperty(error, 'error')) {
                        message += ` ${error.body.error as string}`;
                    }
                    Toast.error(message);
                }
                dispatch(
                    receiveMediaError({
                        search_context: searchContext,
                        folderId,
                    }),
                );
            });
    };
};

/**
 * A function that triggers the media search process
 * @param {string} search_context - value from SEARCH_CONTEXT enum, which type of search should we trigger?
 * @param {boolean} append_results - whether to append the new results or to clear the existing data
 * @param {Number} duration - the current duration of the video (only necessary for audio searches)
 * @param {boolean} autocorrect - whether or not to autocorrect the query
 * @param {Number} folderId - folderId for which search results need to be updated
 */
export function triggerSearch(
    search_context: SearchContextValueType,
    append_results: boolean,
    duration: number,
    autocorrect = true,
    folderId: number | null = null,
) {
    if (isDAMSearchContext(search_context)) {
        return triggerDAMSearch(search_context, append_results, duration, autocorrect, folderId);
    }
    return triggerMediaSearch(search_context, append_results, duration, autocorrect, folderId);
}

export function triggerSimilarAssetSearch(targetAsset: ImmutableMap<AssetContainerModel>) {
    if (!immutableAssetContainerModelIsBackground(targetAsset)) {
        throw new Error('similar search only works for images and videos');
    }
    const background = targetAsset.get(AssetContainerTypes.BACKGROUND);
    const assetType =
        background.get('type') === SlideBackgroundModelTypeEnum.VIDEO
            ? SimilarAssetSourceTypeEnum.VIDEO
            : SimilarAssetSourceTypeEnum.IMAGE;
    const assetUUID = getAssetUuidFromAssetContainer(targetAsset);
    return (dispatch: AppDispatch) => {
        const data = {
            q: '',
            search_source: MediaSearchSourceEnum.SIMILAR,
            similar_asset_source_type: assetType,
            similar_asset_source_uuid: assetUUID,
        };
        dispatch(setIsSimilarSearchLoading(true));
        ApiService.mediaSearchCreate({ body: data })
            .then((response) => {
                const results = ImmutableFromJS(response.data.results);
                dispatch(setSimilarSearchResults({ results }));
                dispatch(setIsSimilarSearchLoading(false));
                analytics.track('Similar media search results fetched', {
                    assetType,
                    assetUUID,
                });
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                dispatch(setIsSimilarSearchLoading(false));
                const fallbackResult = Immutable.List([targetAsset]);
                dispatch(setSimilarSearchResults({ results: fallbackResult }));
            });
    };
}

// set the audio details and play the track as well
export function setCurrentAudioDetailAndPlay(
    audioUrl: string | null,
    audioSeekPercent = 0,
    shouldPlay = true,
    currentAudioSeekPercent = 0,
    currentDuration = 0,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        if (audioUrl === getPlayingTrackUrl(getState())) {
            // as audio is already playing, so just seek to the correct position.
            dispatch(
                setCurrentPlayingAudioScrubberSeekPercent({
                    scrubberSeekPercent: currentAudioSeekPercent,
                }),
            );
            dispatch(
                setCurrentPlayingAudioSeekPercent({
                    seekPercent: audioSeekPercent,
                }),
            );
        }
        // it's a new audio, so set the audio and the seek to the correct position.
        else {
            dispatch(
                setCurrentPlayingAudioScrubberSeekPercent({
                    scrubberSeekPercent: currentAudioSeekPercent,
                }),
            );
            dispatch(setCurrentPlayingAudioUrl({ audioUrl }));
            dispatch(
                setCurrentPlayingAudioSeekPercent({
                    seekPercent: audioSeekPercent,
                }),
            );
        }

        dispatch(setCurrentPlayingAudioDuration({ duration: currentDuration }));
        dispatch(setCurrentAudioPlayingStatus({ isPlaying: shouldPlay }));
    };
}

export function setCurrentPlayingAudioDurationPercent(currentTime: number, duration: number) {
    return (dispatch: AppDispatch) => {
        const scrubberSeekPercent = parseFloat(((currentTime / duration) * 100).toFixed(2));

        dispatch(setCurrentPlayingAudioDuration({ duration: currentTime }));
        dispatch(setCurrentPlayingAudioScrubberSeekPercent({ scrubberSeekPercent }));
    };
}

export function storeUploadTagsOnServer(
    assetId: number,
    assetType: AssetTypes,
    assetTags: string[],
    attribution?: string | null,
) {
    return ApiService.uploadsMetadataUpdateUpdate({
        body: {
            asset_id: assetId,
            asset_type: assetType,
            asset_tags: assetTags,
            ...(attribution !== undefined && { attribution }),
        },
    });
}

export function setActiveUploadsFolder(folderId: number | null = null) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const searchContext = getActiveSearchContextForCurrentTab(state);
        if (searchContext === undefined) {
            throw new Error('No active search context found for setting active uploads folder');
        }
        const currentSearchTerm = getActiveSearchTerm(state, searchContext);
        const lastActiveFolderId = selectActiveMediaFolder(state, searchContext);
        const selectedFolderDetails = getFolderDetailsById(state, folderId);

        dispatch(setActiveFolder({ folderId, searchContext })); // this can set or reset the current folder selection

        if (folderId && selectedFolderDetails?.get('page') === 1) {
            // trigger api call to get folder specific assets if not loaded previously
            // else the call to load assets will be delegated to BackgroundMediaAssetList.
            // ensure we clear results before fetching since we're starting from the beginning
            dispatch(triggerSearch(searchContext, false, 0, false, folderId));
        }

        dispatch(fetchFrequentlyUsedTags(searchContext, folderId));

        /**
         * When navigating out of a folder after a search operation,
         * clear the search term and reset the page no to reset the folder state and remove last searched items.
         */
        if (!folderId && currentSearchTerm && lastActiveFolderId) {
            dispatch(
                changeMediaSearchTerm({
                    search_context: searchContext,
                    search_term: '',
                }),
            );
            dispatch(resetFolderSearchState({ folder_id: lastActiveFolderId }));
        }
    };
}

export function fetchFrequentlyUsedTags(
    searchContext: SearchContextValueType,
    folderId: number | null,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();

        if (searchContext !== SEARCH_CONTEXT.UPLOADED_VISUALS) {
            return; // frequently used tags is only available for uploaded images/videos
        }

        const frequentlyUsedTags = getFrequentlyUsedTags(state, searchContext, folderId);
        if (frequentlyUsedTags !== null) {
            return;
        }

        const activeMediaSourceFilters = getActiveMediaFilters(state, searchContext);
        ApiService.topUploadsTagsRetrieve({
            query: {
                filter: activeMediaSourceFilters.join(','),
                folder_id: folderId ?? undefined,
            },
        })
            .then((response) => {
                dispatch(
                    storeFrequentlyUsedTags({
                        folderId,
                        search_context: searchContext,
                        frequently_used_tags: ImmutableFromJS(response.data.top_tags),
                    }),
                );
            })
            .catch((error: unknown) => {
                // log the error to sentry, don't show an error to the user
                ErrorReporter.capture(error);
            });
    };
}

export function loadAllFolders() {
    return (dispatch: AppDispatch) => {
        ApiService.mediaFoldersList()
            .then((response) => {
                const uploadFolders = ImmutableFromJS(response.data).map((folder) =>
                    setUploadsFolderStore(folder),
                );
                dispatch(storeAllFolders({ folders: uploadFolders }));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading folders');
            });
    };
}

export function changeFolderForAsset(
    asset: ImmutableMap<AssetContainerModel>,
    targetFolderId: number,
) {
    let assetType: AssetTypes | null = null;
    let assetId: number | null = null;
    if (immutableAssetContainerModelIsBackground(asset)) {
        const background = asset.get(AssetContainerTypes.BACKGROUND);
        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
            assetId = background.get(SlideBackgroundModelTypeEnum.IMAGE).get('id');
            assetType = AssetTypes.IMAGE;
        } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            assetId = background.get(SlideBackgroundModelTypeEnum.VIDEO).get('id');
            assetType = AssetTypes.VIDEO;
        }
    } else if (immutableAssetContainerModelIsIcon(asset)) {
        assetId = asset.get(AssetContainerTypes.ICON).get('id');
        assetType = AssetTypes.ICON;
    }

    if (assetId === null || assetType === null) {
        throw new Error('Could not get the asset out of the background asset when changing folder');
    }

    return ApiService.uploadsMetadataUpdateUpdate({
        body: {
            asset_id: assetId,
            asset_type: assetType,
            folder_id: targetFolderId,
        },
    });
}

export function createNewFolder(
    folderName: string,
    creation_source: SearchContextValueType = SEARCH_CONTEXT.UPLOADED_VISUALS,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        let response = null;
        try {
            response = await ApiService.mediaFoldersCreate({
                body: {
                    name: folderName,
                    creation_source,
                },
            });
        } catch (error) {
            ErrorReporter.capture(error);
            Toast.error('Could not create a folder, Please try again.');
            return;
        }

        const folder = ImmutableFromJS(response.data);
        dispatch(addNewFolder({ folder }));
        const newState = getState();
        if (getActiveSearchContextForCurrentTab(newState) === creation_source) {
            // if the user hasn't switched to a new tab while the folder was being created,
            // open the folder
            dispatch(setActiveUploadsFolder(folder.get('id')));
        }
    };
}

export function getOrCreateRecordingFolder(folderName: string = RECORDING_FOLDER_NAME) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const recordingFolder = selectRecordingFolder(state, folderName);
        if (recordingFolder) {
            return recordingFolder;
        } else {
            // create and save the folder to redux
            const response = await ApiService.mediaFoldersCreate({
                body: {
                    name: folderName,
                    creation_source: SEARCH_CONTEXT.UPLOADED_VISUALS,
                },
            });
            dispatch(addNewFolder({ folder: ImmutableFromJS(response.data) }));

            // now select the new folder from the updated redux
            const newState = getState();
            return selectRecordingFolder(newState, folderName);
        }
    };
}

export function getOrCreateMediaFolder(folderName: string = SCRIPT_COMPOSER_MEDIA_FOLDER_NAME) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const existingFolder = selectFolderByName(state, folderName);
        if (existingFolder) {
            return existingFolder;
        } else {
            // create and save the folder to redux
            const response = await ApiService.mediaFoldersCreate({
                body: {
                    name: folderName,
                    creation_source: SEARCH_CONTEXT.UPLOADED_VISUALS,
                },
            });
            dispatch(addNewFolder({ folder: ImmutableFromJS(response.data) }));

            // now select the new folder from the updated redux
            const newState = getState();
            return selectFolderByName(newState, folderName);
        }
    };
}

export function createFolderForFiles(
    files: File[],
    uploadOpts: UploadOptions,
    folderCreationSource: UploadSearchContextType,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();

        const folderMetadata: {
            folderName: string;
            folderId: number | null;
            isNewFolder: boolean;
        } = {
            folderName: '',
            folderId: null,
            isNewFolder: true,
        };

        files.forEach((file) => {
            const dirShape = file.webkitRelativePath.split('/');
            const folderName = dirShape[0];
            folderMetadata['folderName'] = folderName;
            folderMetadata['folderId'] = null;
            folderMetadata['isNewFolder'] = true;

            // check if the folder was previously created by the user.
            // If you see performance issues, you can either remove this check and just use the first folder name OR memioize the selector funtion call.
            const previouslyCreatedFolder = getFolderDetailsByNameAndSource(
                state,
                folderName,
                folderCreationSource,
            );
            if (previouslyCreatedFolder) {
                const oldFolderId = previouslyCreatedFolder.get('id');
                folderMetadata['folderId'] = oldFolderId;
                folderMetadata['isNewFolder'] = false;
            }
        });

        if (!folderMetadata['folderId']) {
            let response = null;
            try {
                response = await ApiService.mediaFoldersCreate({
                    body: {
                        name: folderMetadata.folderName,
                        creation_source: folderCreationSource,
                    },
                });
            } catch (e) {
                Toast.error('Unable to create a folder, please try again.');
                throw e;
            }
            dispatch(addNewFolder({ folder: ImmutableFromJS(response.data) }));
            Toast.success(
                `Folder "${response.data.name}" created successfully. Now uploading all file(s) in the folder`,
            );
            folderMetadata['folderId'] = response.data.id;
        }

        // Update the folder id in the file object.
        const fileList = files.map(
            (file) =>
                ({
                    file,
                    metadata: { ...folderMetadata },
                }) as FileWithMetadata,
        );

        uploadOpts['file_list'] = fileList;
        return uploadOpts;
    };
}

export function fetchMediaQueryRecommendations(sceneText: string, script: string) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        dispatch(showMediaQuerySuggestionError({ show: false }));
        dispatch(setIsMediaQuerySearchLoading({ isLoading: true }));
        try {
            const task_response = await ApiService.suggestedMediaCreate({
                body: {
                    sentence: sceneText,
                    script: script,
                    search_count: 2,
                },
            });

            const ws = WebSocketClient.getInstance();
            const response = await ws.waitForMessage<SuggestedMediaResponse>(
                task_response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'suggested_media',
            );

            dispatch(
                setMediaQuerySearchResults({
                    searchResults: response.search_results,
                }),
            );
            dispatch(setIsMediaQuerySearchLoading({ isLoading: false }));
        } catch (error) {
            ErrorReporter.capture(error);
            dispatch(setIsMediaQuerySearchLoading({ isLoading: false }));
            dispatch(showMediaQuerySuggestionError({ show: true }));
        }
    };
}

export function getIsSimilarMediaResult(mediaSourceData: ImmutableMap<SlideBackgroundModel>) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const isSimilarSearchContext = getState().media.is_similar_search_context;
        const originalAssetUuid = getSimilarSearchOriginalAssetUuid(getState());

        if (!isSimilarSearchContext || !originalAssetUuid) {
            return false;
        }

        // Check if this is a similar media result (not the original asset)
        const currentAssetUuid =
            mediaSourceData.get('type') === SlideBackgroundModelTypeEnum.VIDEO
                ? mediaSourceData.get(SlideBackgroundModelTypeEnum.VIDEO)?.get('uuid')
                : mediaSourceData.get(SlideBackgroundModelTypeEnum.IMAGE)?.get('uuid');

        return currentAssetUuid !== originalAssetUuid;
    };
}

export function fetchRecommendedSearchTerms(
    script: string,
    systemTriggered = true,
    parentActionGroup = uuid(),
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        function fmt(script: string): SuggestedMediaRequest {
            return {
                sentence: script.split('\n')[0],
                script,
                search_count: 0, // don't want media results, just the queries!
            };
        }
        const MAX_QUERIES = 5; // cap to avoid visual overload
        const num_scenes = script.split('\n').length;
        if (num_scenes < 5) {
            // not enough content to generate useful queries, so abort!
            // also this helps avoid loading scene recommendations based on the template's first scene
            return;
        }
        dispatch(setIsFetchingRecommendedSearchTerms(true));
        try {
            const task_response = await ApiService.suggestedMediaCreate({
                body: fmt(script),
            });

            const ws = WebSocketClient.getInstance();
            const response = await ws.waitForMessage<SuggestedMediaResponse>(
                task_response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'suggested_media',
            );

            // only use the first MAX_QUERIES queries
            const queries = response.queries.slice(0, MAX_QUERIES);
            dispatch(
                setRecommendedSearchTerms({
                    terms: Immutable.List(queries),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        } catch (error) {
            ErrorReporter.capture(error);
        } finally {
            dispatch(setIsFetchingRecommendedSearchTerms(false));
        }
    };
}

export function deleteVoiceoverSuccess(id: number) {
    return (dispatch: AppDispatch) => {
        dispatch(
            deleteAudioSuccess({
                id,
                search_context: SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
            }),
        );
    };
}

export function fetchAllUserFavorites({ search_audio = false } = {}) {
    return (dispatch: AppDispatch) => {
        ApiService.favoritesListMarkedFavoritesList({
            query: { search_audio },
        }).then((response) => {
            dispatch(setAllFavorites({ allFavoriteMedia: response.data }));
        });
    };
}

const saveFavoriteMediaSelection = ({ asset_type, uuid, favorite }: ToggleFavoritedAsset) => {
    const route = favorite
        ? ApiService.favoritesMarkFavoriteCreate
        : ApiService.favoritesUnmarkFavoriteDestroy;
    route({ body: { asset_type, uuid, favorite } });
};

export function toggleFavoriteMedia(
    media: ImmutableMap<AssetContainerModel> | ImmutableMap<AudioAssetModel | AudioSourceData>,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        let mediaId: string | undefined | null;
        let mediaType: AssetTypes | undefined;

        const isImmutableAudioAssetOrAudioSourceData = (
            media: unknown,
        ): media is ImmutableMap<AudioAssetModel | AudioSourceData> => {
            return isImmutableAudioAsset(media) || isImmutableAudioSourceData(media);
        };

        let isAIImage = false;
        if (isImmutableAudioAssetOrAudioSourceData(media)) {
            mediaId = media.get('uuid');
            mediaType = AssetTypes.AUDIO;
        } else if (immutableAssetContainerModelIsBackground(media)) {
            const background = media.get(AssetContainerTypes.BACKGROUND);
            if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
                mediaType = AssetTypes.IMAGE;
                const immutableImageAsset = background.get(SlideBackgroundModelTypeEnum.IMAGE);
                mediaId = immutableImageAsset.get('uuid');
                isAIImage = immutableImageAsset.get('source') === SourceEnum.SOURCE_AI_GENERATED;
            } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
                mediaType = AssetTypes.VIDEO;
                mediaId = background.get(SlideBackgroundModelTypeEnum.VIDEO).get('uuid');
            }
        }

        if (mediaId && mediaType) {
            const isFavoriteMedia = getIsFavouriteMedia(getState(), mediaId);
            const toggledSelection = !isFavoriteMedia;

            if (toggledSelection && isAIImage) {
                // If the media is an unsaved AI media save the media first before toggling setting as favorite
                const generation = getGenerationFromMediaUuid(
                    getState(),
                    mediaId,
                    SlideBackgroundModelTypeEnum.IMAGE,
                );
                // If generation data exists, it's an unsaved image from the current session - save it first
                // If no generation data, it's already saved - proceed directly
                if (generation) {
                    try {
                        dispatch(
                            setSavingAIMedia({ generationUuid: generation.uuid, isSaving: true }),
                        );
                        const imageAsset = await saveAIMedia(generation);
                        mediaId = imageAsset
                            .get(AssetContainerTypes.BACKGROUND)
                            ?.get(SlideBackgroundModelTypeEnum.IMAGE)
                            ?.get('uuid');
                        analytics.track('AI Media Saved', {
                            generation_uuid: generation.uuid,
                        });
                    } finally {
                        dispatch(
                            setSavingAIMedia({ generationUuid: generation.uuid, isSaving: false }),
                        );
                    }

                    if (!mediaId) {
                        throw new Error('No valid UUID found for saved AI media');
                    }
                }
            }
            dispatch(
                toggleFavorite({
                    asset_type: mediaType,
                    uuid: mediaId,
                    favorite: toggledSelection,
                }),
            );
            if (!toggledSelection && isAIImage) {
                // Remove the media from redux
                dispatch(
                    deleteMediaResult({
                        mediaUuid: mediaId,
                        assetType: AssetContainerTypes.BACKGROUND,
                        slideBackgroundType: SlideBackgroundModelTypeEnum.IMAGE,
                        search_context: SEARCH_CONTEXT.FAVORITE_AI_MEDIA,
                    }),
                );
            }
            analytics.track(`Favorites: Step: Media ${toggledSelection ? 'Added' : 'Removed'}`, {
                type: mediaType,
                id: mediaId,
            });

            // persist the favorite in server.
            saveFavoriteMediaSelection({
                asset_type: mediaType,
                uuid: mediaId,
                favorite: toggledSelection,
            });
        } else {
            Toast.error('Sorry, we were not able to favorite this media.');
            ErrorReporter.capture(
                new Error('Media type or media id is missing while toggling favorite media'),
            );
        }
    };
}

export function refreshRecommendedAssetsAfterLoadingRecommendedVideo() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // if we've just saved a recommended video then our recommended ("curated") results can be updated
        // so if we're in that view trigger a new search
        const search_context = getActiveSearchContextForTab(getState(), EDITOR_TAB_OPTIONS.MEDIA);
        const search_term =
            search_context !== undefined
                ? getActiveSearchTerm(getState(), search_context)
                : undefined;
        if (search_context === SEARCH_CONTEXT.RECOMMENDED_VISUALS && search_term === '') {
            dispatch(
                triggerMediaSearch(
                    search_context,
                    false, // append results
                    0, // duration, not used in this case
                    false, // autocorrect
                    null, // folderId, not used in this case
                ),
            );
        }
    };
}

export function loadAIMediaModels() {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            if (getAIMediaModels(getState()).length > 0) {
                return;
            }
            const response = await ApiService.aiMediaModelsList();
            dispatch(setAIMediaModels(response.data));
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            throw error;
        }
    };
}

export function generateVideo(params: {
    prompt: string;
    model: VideoGenerationRequestModelEnum;
    aspectRatio: string;
    duration: number;
    expectedCost: number | undefined;
    assetUuids?: string[];
    videoAssetUuid?: string;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const { prompt, model, aspectRatio, duration, assetUuids, expectedCost, videoAssetUuid } =
            params;

        let succeeded: boolean | undefined;
        let taskId: string | undefined;
        try {
            if (getIsInitializingVideoGeneration(getState())) {
                throw new Error('Video generation already in progress');
            }
            dispatch(setIsInitializingVideoGeneration(true));
            try {
                const taskResponse = await ApiService.videoGenerationCreate({
                    body: {
                        prompt,
                        model,
                        aspect_ratio: aspectRatio,
                        duration,
                        asset_uuids: assetUuids ?? [],
                        video_asset_uuid: videoAssetUuid,
                    },
                });
                taskId = taskResponse.data.task_id;
                dispatch(
                    updateAIMediaCreditsUsedInCycle(
                        taskResponse.data.ai_media_credits_used_in_cycle,
                    ),
                );
            } finally {
                dispatch(setIsInitializingVideoGeneration(false));
            }

            dispatch(
                setVideoGenerationTask({
                    taskId,
                    status: TaskResultStatusEnum.PENDING,
                }),
            );

            const ws = WebSocketClient.getInstance();
            const wsResponse = await ws.waitForMessage<SlideBackgroundModel>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'video_generation',
            );

            dispatch(
                setVideoGenerationTask({
                    taskId,
                    status: TaskResultStatusEnum.SUCCESS,
                    result: wsResponse,
                }),
            );
            succeeded = true;
            return wsResponse;
        } catch (error: unknown) {
            succeeded = false;
            Toast.error(
                'Credits used for this task have been refunded to your account.',
                'An error occurred during generation',
            );
            if (expectedCost !== undefined) {
                // Update the usage credits used to reflect the credits that were refunded
                // This is only done here for instant feedback to the user, the actual update is done in the task failure handler on the backend
                const usageLimit = getUsageLimits(getState());
                const usedCredits = usageLimit.ai_media_credits_used_in_cycle;
                const creditLimit = usageLimit.ai_media_credits_limit_per_cycle;
                if (usedCredits !== undefined && ![0, null].includes(creditLimit)) {
                    dispatch(updateAIMediaCreditsUsedInCycle(usedCredits - expectedCost));
                }
            }
            if (taskId) {
                dispatch(
                    setVideoGenerationTask({
                        taskId,
                        status: TaskResultStatusEnum.FAILURE,
                    }),
                );
            }
            throw error;
        } finally {
            dispatch(setIsInitializingVideoGeneration(false));
            analytics.track('Video Generation Completed', {
                model,
                aspect_ratio: aspectRatio,
                duration,
                asset_uuids: assetUuids ?? [],
                succeeded,
            });
        }
    };
}

export function generateImage({
    prompt,
    model,
    videoId,
    aspectRatio,
    assetUuids,
    saveGeneration,
}: {
    prompt: string;
    model: ImageGenerationRequestModelEnum;
    videoId: number;
    aspectRatio?: SupportedAIMediaAspectRatios;
    assetUuids?: string[];
    saveGeneration?: boolean;
}) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        let succeeded: boolean | undefined;
        let taskId: string | undefined;
        try {
            if (getIsInitializingImageGeneration(getState())) {
                throw new Error('Image generation already in progress');
            }
            dispatch(setIsInitializingImageGeneration(true));
            try {
                const taskResponse = await ApiService.imageGenerationCreate({
                    body: {
                        prompt,
                        model,
                        video_id: videoId,
                        aspect_ratio: aspectRatio,
                        asset_uuids: assetUuids ?? [],
                        save_generation: saveGeneration || true,
                    },
                });
                taskId = taskResponse.data.task_id;
                dispatch(
                    updateImageGenerationsUsedInCycle(
                        taskResponse.data.image_generations_used_in_cycle,
                    ),
                );
            } finally {
                dispatch(setIsInitializingImageGeneration(false));
            }

            const ws = WebSocketClient.getInstance();
            const wsResponse = await ws.waitForMessage<SlideBackgroundModel>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'image_generation',
            );

            const generationResult = {
                result: {
                    // taskId is MediaGeneration.uuid on the backend; wsResponse.image.uuid is the ImageAsset uuid (different).
                    uuid: taskId,
                    asset: ImmutableToJS(
                        convertImmutableSlideBackgroundToImmutableAssetContainer(
                            ImmutableFromJS(wsResponse),
                        ),
                    ),
                    prompt,
                    model_name: model as unknown as ModelNameEnum,
                    properties: null,
                },
                error: null,
            };

            if (assetUuids?.length) {
                // Image-to-image scenario - dispatch to aiSimilarGenerations
                dispatch(
                    setAISimilarGenerations({
                        results: [generationResult],
                        append: false,
                    }),
                );
            } else {
                // Text-to-image scenario - dispatch to currentMediaGenerations
                dispatch(setCurrentMediaGenerations([generationResult]));
            }

            succeeded = true;
            return wsResponse;
        } catch (error: unknown) {
            succeeded = false;
            Toast.error('An error occurred while generating images', 'Generation failed');
            dispatch(loadUsageLimits(videoId));
            throw error;
        } finally {
            dispatch(setIsInitializingImageGeneration(false));
            analytics.track('Image Generation Completed', {
                model,
                succeeded,
            });
        }
    };
}

export function changeActiveAIMediaSubTab(subTab: AI_MEDIA_SUB_TAB_OPTIONS) {
    return (dispatch: AppDispatch) => {
        dispatch(setActiveAIMediaSubTab(subTab));
        if (subTab === AI_MEDIA_SUB_TAB_OPTIONS.FAVORITES) {
            dispatch(
                changeActiveSearchContext({ search_context: SEARCH_CONTEXT.FAVORITE_AI_MEDIA }),
            );
        } else if (subTab === AI_MEDIA_SUB_TAB_OPTIONS.CHARTS) {
            dispatch(
                changeActiveSearchContext({ search_context: SEARCH_CONTEXT.AI_GENERATED_CHARTS }),
            );
            dispatch(
                toggleFilterByUserLevel({
                    search_context: SEARCH_CONTEXT.AI_GENERATED_CHARTS,
                    filter_by_user_level: FILTER_BY_LEVELS.filter_by_user_level,
                }),
            );
        } else {
            dispatch(
                changeActiveSearchContext({ search_context: SEARCH_CONTEXT.AI_GENERATED_MEDIA }),
            );
            dispatch(
                toggleFilterByUserLevel({
                    search_context: SEARCH_CONTEXT.AI_GENERATED_MEDIA,
                    filter_by_user_level: FILTER_BY_LEVELS.filter_by_user_level,
                }),
            );
        }
    };
}
